import { ref } from '@nuxtjs/composition-api';
import { ERROR_CODE } from '@/constants/ErrorCodes'
import useTlmCart from '~/composable/useTlmCart';
import useTracking from '~/composable/tracking/useTracking';
import useMitID from '@/composable/useMitID';
import useCheckout from '@/composable/checkout/useCheckout';
import useSubscriptions from '@/composable/checkout/validations/useSubscriptions';

export default function useChangeSubscriptionValidation (app) {
  const { getCartID, mixPoList, subscriptionList } = useTlmCart(app)
  const { track } = useTracking()
  const { getMitIdToken } = useMitID(app)
  const { state } = useCheckout(app)
  const { preValidateChangeSubscription, isSubscriptionUpgrade } = useSubscriptions(app)
  const subscription = state?.subscription
  const { changeSubscription } = subscription;

  // set defaults
  const isLoggedIn = ref(!!getMitIdToken())

  const identifier = ref({
    accessTokenType: 'MIT_ID',
    accessToken: getMitIdToken() || '',
    userName: '',
    password: ''
  })
  const selected = ref(null)
  const showCvrNoMatch = ref(false)
  const showOwnerError = ref(false)
  const showChangeInProgressError = ref(false)
  const showNegativeBalanceError = ref(false)
  const showBindingError = ref(false)
  const showMultipleBindingError = ref(false)
  const showUserInQuarantineError = ref(false)
  const showPlayEmailInUseError = ref(false)
  const errorCode = ref('')
  const hasError = ref(false)
  const isProcessing = ref(false)
  const removedServices = ref(null)
  const preValidate = ref(null)
  const currentSelectedSubscription = ref(null)

  // error handling - negative balance
  // @todo use signup blocking notifications object instead
  const checkNegativeBalance = (response) => {
    let error;
    showNegativeBalanceError.value = !!(response.hasErrors && (error = response.errors?.find(error => error.notificationCode === ERROR_CODE.CHANGE_SUB_NEGATIVE_BALANCE_1 ||
      error.notificationCode === ERROR_CODE.CHANGE_SUB_NEGATIVE_BALANCE_2)))
    if (error) {
      hasError.value = true;
      errorCode.value = error.notificationCode;
      track('errorMsg', {
        interactionComponent: 'changeSubscription',
        msg: [ERROR_CODE.CHANGE_SUB_NEGATIVE_BALANCE_1, 'CHANGE_SUB_NEGATIVE_BALANCE_1']
      })
    }
  }

  const checkNoMatch = (response) => {
    showCvrNoMatch.value = !!(response.hasErrors && response.errors?.find(error => error.notificationCode === ERROR_CODE.CVR_NO_MATCH))
    if (showCvrNoMatch.value) {
      hasError.value = true;
      track('errorMsg', { interactionComponent: 'changeSubscription', msg: [ERROR_CODE.CVR_NO_MATCH, 'CVR_NO_MATCH'] })
    }
  }

  const checkOwnerError = (response) => {
    showOwnerError.value = !!(response.hasErrors && response.errors?.find(error => error.notificationCode === ERROR_CODE.SUB_USER_1 ||
      error.notificationCode === ERROR_CODE.SUB_USER_2))
    if (showOwnerError.value) {
      hasError.value = true;
      track('errorMsg', { interactionComponent: 'changeSubscription', msg: [ERROR_CODE.CVR_NO_MATCH, 'CVR_NO_MATCH'] })
    }
  }

  const checkBinding = (response) => {
    showBindingError.value = !!(response.hasErrors && response.errors?.find(error => error.notificationCode === ERROR_CODE.BINDING))
    if (showBindingError.value) {
      hasError.value = true;
      track('errorMsg', { interactionComponent: 'changeSubscription', msg: [ERROR_CODE.BINDING, 'BINDING'] })
    }
  }

  const checkMultipleBinding = (response) => {
    let error;
    showMultipleBindingError.value = !!(response.hasErrors && (error = response.errors?.find(err =>
      err.notificationCode === ERROR_CODE.MULTIPLE_BINDING ||
      err.notificationCode === ERROR_CODE.MULTIPLE_BINDING_WITH_FAD
    )))
    if (error) {
      hasError.value = true;
      errorCode.value = error.notificationCode;
      const label = error.notificationCode === ERROR_CODE.MULTIPLE_BINDING_WITH_FAD ? 'MULTIPLE_BINDING_WITH_FAD' : 'MULTIPLE_BINDING'
      track('errorMsg', { interactionComponent: 'changeSubscription', msg: [error.notificationCode, label] })
    }
  }

  const findErrorByCodes = (response, isMatch) => {
    const lists = [response?.errors, response?.actionErrors]
    for (const list of lists) {
      if (!Array.isArray(list)) {
        continue
      }
      const match = list.find(err => isMatch(err?.notificationCode))
      if (match) {
        return match
      }
    }
    return null
  }

  const isChangeInProgressCode = (notificationCode) => {
    return notificationCode === ERROR_CODE.CHANGE_IN_PROGRESS ||
      notificationCode === ERROR_CODE.CHANGE_IN_PROGRESS_PACKAGE ||
      notificationCode === ERROR_CODE.CHANGE_IN_PROGRESS_MARKET_OFFER
  }

  const checkChangeInProgressError = (response) => {
    const error = findErrorByCodes(response, isChangeInProgressCode)
    const shouldShow = Boolean(error)
    showChangeInProgressError.value = shouldShow
    if (shouldShow) {
      hasError.value = true
      errorCode.value = error.notificationCode
      track('errorMsg', {
        interactionComponent: 'changeSubscription',
        msg: [error.notificationCode, 'CHANGE_IN_PROGRESS']
      })
    }
  }
  const checkPlayEmailInUseError = (response) => {
    let error;
    showPlayEmailInUseError.value = !!(response.hasErrors && (error = response.errors?.find(error => error.notificationCode === ERROR_CODE.PLAY_EMAIL_IN_USE)))
    if (error) {
      hasError.value = true;
      errorCode.value = error.notificationCode;
      track('errorMsg', {
        interactionComponent: 'changeSubscription',
        msg: [ERROR_CODE.PLAY_EMAIL_IN_USE, 'PLAY_EMAIL_IN_USE']
      })
    }
  }
  const checkUserInQuarantineError = (response) => {
    showUserInQuarantineError.value = !!(response.hasErrors && (response.warnings?.find(error => error.notificationCode === ERROR_CODE.QUARANTINE)))
    if (showUserInQuarantineError.value) {
      hasError.value = true;
      track('errorMsg', { interactionComponent: 'changeSubscription', msg: [ERROR_CODE.BINDING, 'BINDING'] })
    }
  }

  // format payload - for selecting subscription
  const formatPayload = (mobileNumber, esimOnlyDevice) => {
    return {
      accessToken: identifier.value.accessToken,
      accessTokenType: identifier.value.accessTokenType,
      bundleID: subscriptionList.value.sku,
      phoneNumber: mobileNumber,
      esimOnlyDevice,
      cartID: getCartID.value,
      mixServices: mixPoList.value,
      userName: identifier.value.userName,
      password: identifier.value.password
    }
  }
  const onSubscriptionsReady = res => {
    if (res.accessTokenType === 'UN_PW') {
      isLoggedIn.value = true
      identifier.value.accessTokenType = 'UN_PW';
      identifier.value.accessToken = res?.accessToken;
      identifier.value.userName = res?.userName;
      identifier.value.password = res?.password;
      changeSubscription.accessTokenType = 'UN_PW'
      changeSubscription.accessToken = res?.accessToken
      changeSubscription.userName = res?.userName || ''
      changeSubscription.password = res?.password || ''
      changeSubscription.subscriptions = res?.subscriptions?.value.filter(item => item.bundleID.substr(0, 3) !== 'MBB') || []
    }
  }
  // select subscription
  const onSubscriptionSelected = async event => {
    hasError.value = false
    isProcessing.value = true
    selected.value = event
    // bails if unselected - and reset
    if (event === undefined) {
      removedServices.value = null
      preValidate.value = null
      changeSubscription.number = ''
      changeSubscription.isUpgrade = null
      changeSubscription.bundleName = ''
      currentSelectedSubscription.value = null
      isProcessing.value = false
      hasError.value = false
      showNegativeBalanceError.value = false
      showCvrNoMatch.value = false;
      showOwnerError.value = false;
      showChangeInProgressError.value = false;
      return
    }

    currentSelectedSubscription.value = changeSubscription.subscriptions[event]
    const { phoneNumber, bundleName, bundleID } = currentSelectedSubscription.value
    const res = await preValidateChangeSubscription(formatPayload(phoneNumber))
    const { responseObject } = res
    removedServices.value = responseObject.productList.filter(product => product.action === 'REMOVE' && product.poName !== bundleID)
    preValidate.value = res
    changeSubscription.number = phoneNumber
    changeSubscription.isUpgrade = isSubscriptionUpgrade
    changeSubscription.bundleName = bundleName
    checkNoMatch(res);
    checkNegativeBalance(res)
    checkBinding(res)
    checkMultipleBinding(res)
    checkOwnerError(res)
    checkChangeInProgressError(res);
    checkUserInQuarantineError(res)
    checkPlayEmailInUseError(res)
    isProcessing.value = false
  }
  return {
    selected,
    currentSelectedSubscription,
    preValidate,
    removedServices,
    isProcessing,
    onSubscriptionSelected,
    onSubscriptionsReady,
    hasError,
    isLoggedIn,
    identifier,
    showUserInQuarantineError,
    showNegativeBalanceError,
    showBindingError,
    showMultipleBindingError,
    showCvrNoMatch,
    showOwnerError,
    showChangeInProgressError,
    showPlayEmailInUseError,
    errorCode,
    checkNegativeBalance,
    checkNoMatch,
    checkOwnerError,
    checkBinding,
    checkMultipleBinding,
    checkChangeInProgressError,
    checkUserInQuarantineError,
    checkPlayEmailInUseError,
    formatPayload
  }
}
